import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, ContentfulBody } from '../../Elements';
import * as Inputs from '../Inputs';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  CHRISTMAS_INPUT,
  quizAnswerHistoryState,
  quizAnswersState,
  quizHistoryStepState,
  quizIdState,
  quizInputState,
  quizResultsState,
} from 'utils/recoil/quiz';
import clsx from 'clsx';
import { useMediaQuery } from 'utils/hooks/useMediaQuery';
import NoozApi from '../../../../services/NoozApi';
import { formatContentfulComponentProps } from 'utils/globals';

const PRODUCT_CATEGORY = {
  READING: '7qOeTXE5sQwylAIjXgtxO2',
  SUN: '13g1oRpy0JrvhAedFuTPCX',
  BLUELIGHT: '2pM1w5oM1J69d3trRefhs7',
  KIDS: '9wQEkyz1VK22c87UReQ9r',
};

const InputOptionsFilter = {
  form: (form, products) => {
    if (form.screen === 'bluelight')
      return ['rectangular', 'oval', 'round', 'square'];

    if (form.screen === 'sun') return ['round', 'square'];

    if (form.screen === 'reading')
      return form.screenTime && form.screenTime !== 'never'
        ? ['rectangular', 'oval', 'round', 'square']
        : ['rectangular', 'oval', 'round', 'square', 'butterfly'];

    return undefined;
  },
};
const returnCategoryShopItemsUrl = (id) => `/productcategory/${id}/shopitems`;
const usePaddingToBottom = (elRef) => {
  const [padding, setPadding] = useState<number | string>(0);
  const observer = React.useRef(
    new ResizeObserver((entries) => {
      if (entries[0] && entries[0].target.clientHeight) {
        const footer = entries[0].target;

        setPadding(`calc(${footer?.offsetHeight}px + 1rem)`);
      }
    })
  );

  React.useEffect(() => {
    if (elRef.current) {
      observer.current.observe(elRef.current);
    }

    return () => {
      if (elRef?.current) observer.current?.unobserve(elRef.current);
    };
  }, [elRef, observer]);

  return padding;
};

export const QuizForm = ({ form, reset, finish, gaTag: loadTag }) => {
  const subButtons = useMemo(
    () => formatContentfulComponentProps(form.subForm),
    [form]
  );
  const [state, setState] = useState({});
  const results = useRecoilValue(quizResultsState);
  const setQuizResults = useSetRecoilState(quizResultsState);

  const [quizAnswerState, setQuizAnswerState] = useRecoilState(
    quizAnswersState
  );
  const [quizHistoryStep, setQuizHistoryStep] = useRecoilState(
    quizHistoryStepState
  );
  const [quizAnswerHistory, setQuizAnswerHistoryState] = useRecoilState(
    quizAnswerHistoryState
  );

  const setQuizId = useSetRecoilState(quizIdState);
  const quizInput = useRecoilValue(quizInputState);
  const isFirstStep = quizHistoryStep === 0;

  useEffect(() => {
    setQuizId('christmas');

    if (typeof ga === 'undefined') {
      return;
    }
    ga(
      'send',
      'event',
      'Quiz',
      loadTag?.text || 'Start',
      loadTag?.text || 'Start'
    );
  }, []);

  const activeInput = useMemo(() => {
    const input = form.inputs.find(
      (input) => input.contentful_id === quizInput?.input
    );
    const Input = Inputs[input?.reference];

    const onClick = (value) => {
      setState({
        [input.name]: value,
      });
    };

    if (Input) {
      return (
        <Input
          key={JSON.stringify(quizAnswerState)}
          {...input}
          {...subButtons}
          value={state[input.name]}
          onChange={onClick}
          filteredOptions={InputOptionsFilter[input.name]?.(
            quizAnswerState,
            results
          )}
        />
      );
    }
  }, [quizInput, state, form, subButtons, results, quizAnswerState]);
  const inputProps = useMemo(
    () => form.inputs.find((input) => input.contentful_id === quizInput?.input),
    [form, quizInput]
  );

  const handleBackOnclick = () => {
    if (!isFirstStep) {
      setQuizAnswerState(quizAnswerHistory[quizHistoryStep - 1]);
      setQuizAnswerHistoryState((history) => {
        let historyCopy = [...history];
        historyCopy.pop();
        return historyCopy;
      });
      setQuizHistoryStep((s) => s - 1);
    }
  };

  const handleOnclick = () => {
    const newState = { ...quizAnswerState, ...state };
    setQuizAnswerState(newState);
    setQuizAnswerHistoryState([...quizAnswerHistory, newState]);
    setQuizHistoryStep((s) => s + 1);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1);
    if (quizHistoryStep + 1 === parseInt(quizInput?.totalQuestions)) finish();

    if (
      [CHRISTMAS_INPUT.SCREEN_STD, CHRISTMAS_INPUT.SCREEN_SENIOR].includes(
        quizInput.input
      )
    ) {
      let category;

      if (newState?.age === 'kid') {
        category = PRODUCT_CATEGORY.KIDS;
      } else if (newState?.screen === 'sun') {
        category = PRODUCT_CATEGORY.SUN;
      } else if (newState?.screen === 'bluelight') {
        category = PRODUCT_CATEGORY.BLUELIGHT;
      } else if (newState?.screen === 'reading') {
        category = PRODUCT_CATEGORY.READING;
      }

      if (category)
        NoozApi.get(returnCategoryShopItemsUrl(category))
          .then((res) => {
            setQuizResults(res.data);
          })
          .catch((e) => {
            console.log(e);
          });
    }
  };

  const isMobile = useMediaQuery('md');
  const hasAnswer = !!state[
    form.inputs.find((input) => input.contentful_id === quizInput?.input)?.name
  ];
  const bottomDivRef = useRef();
  const paddingBottom = usePaddingToBottom(bottomDivRef);

  return (
    <div className={'flex flex-col w-full h-full'}>
      <div className='w-full bg-gray-200 h-1'>
        <div
          className={`flex-1 bg-redChristmas h-1 transform ease-in-out duration-300`}
          style={{
            width: `${((quizHistoryStep + 1) / quizInput?.totalQuestions) *
              100}%`,
          }}
        />
      </div>
      <div className={'flex-1 flex flex-col'} style={{ paddingBottom }}>
        <form
          className={clsx(
            'flex-1 w-full flex flex-col items-center text-center pt-8'
          )}
        >
          <div className={'relative w-full flex justify-center'}>
            <p
              className={clsx(
                'w-fit px-4 py-1 bg-redChristmas',
                'md:text-center',
                'text-white',
                'font-bold',
                'font-gilmer',
                'text-xs',
                'uppercase',
                'mb-4',
                'px-4'
              )}
            >
              {`Question ${quizHistoryStep + 1}`}
              <span
                className={'text-white font-light'}
              >{`/${quizInput?.totalQuestions}`}</span>
            </p>
            <div className='fixed left-4 md:left-8 top-8'>
              <Button
                onClick={isFirstStep ? reset : handleBackOnclick}
                type='contained'
                size='base'
                color={{ classname: 'black' }}
                icon={{
                  iconKey: 'HiChevronLeft',
                  size: '2xl',
                }}
              />
            </div>
          </div>

          <React.Fragment key={quizInput.input}>{activeInput}</React.Fragment>
        </form>
      </div>
      <div
        id={'form-footer'}
        ref={bottomDivRef}
        className={clsx(
          'flex',
          'flex-col',
          'md:flex-row',
          'justify-center items-center',
          'space-y-8',
          'md:space-y-0 md:space-x-8',
          'fixed bottom-0 p-4 border-t bg-white',
          'w-full'
        )}
      >
        {inputProps?.body && ((!hasAnswer && isMobile) || !isMobile) ? (
          <div className={clsx('max-w-xs text-center md:text-left')}>
            <ContentfulBody body={inputProps?.body} />
          </div>
        ) : null}
        {(hasAnswer && isMobile) ||
        (isMobile && !inputProps?.body) ||
        !isMobile ? (
          <div>
            <Button
              disabled={!hasAnswer}
              onClick={handleOnclick}
              icon={{ iconKey: 'HiChevronRight' }}
              label={{
                text:
                  quizHistoryStep + 1 === parseInt(quizInput?.totalQuestions)
                    ? form?.submitButton?.label?.text
                    : form?.secondaryButton?.label?.text || 'Next',
              }}
              type={!hasAnswer ? 'outlined' : 'contained'}
              color={{
                classname: 'redChristmas',
              }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};
